import { StrictMode, Suspense, useEffect, memo } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LogtoProvider } from "@logto/react";

import { lazy } from "react";
import { LoginPage, CallbackPage, RequestAccessPage } from "./components/auth";

// Lazy-load main App component
const App = lazy(() => import("./App"));
import { LogtoStreamingProvider } from "./components/auth/LogtoStreamingProvider";
import { useAuthStore } from "./stores/authStore";
import {
  getLogtoConfig,
  isLogtoEnabled,
  SessionLogtoClient,
} from "./services/auth/logto";

// Create a TanStack Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Import i18n configuration
import "./i18n";

// Import global styles
import "./styles/globals.css";

// Initialize theme synchronously before React renders to prevent flash
function initializeThemeBeforeRender() {
  try {
    const stored = localStorage.getItem("lexe-ui-storage");
    if (stored) {
      const parsed = JSON.parse(stored);
      const theme = parsed?.state?.theme || "dark";
      document.documentElement.classList.toggle("dark", theme === "dark");
      document.documentElement.setAttribute("data-theme", theme);
    } else {
      // Default to dark mode
      document.documentElement.classList.add("dark");
      document.documentElement.setAttribute("data-theme", "dark");
    }
  } catch {
    // Default to dark mode on error
    document.documentElement.classList.add("dark");
    document.documentElement.setAttribute("data-theme", "dark");
  }
}

// Run before React renders
initializeThemeBeforeRender();

// Loading fallback
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-leo-accent border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground text-sm">Loading LEXe...</p>
      </div>
    </div>
  );
}

// Import Logto hook for protected routes
import { useLogto } from "@logto/react";

// Module-level flag: survives component unmount/remount.
// Once user is authenticated, never go back to loading/redirect state.
// This breaks the infinite loop caused by Logto SDK toggling isLoading
// during token refresh (getAccessToken → isLoading=true → unmount → remount → repeat).
let _everAuthenticated = false;

/**
 * Blocks re-render cascade from useLogto() isLoading toggles.
 * Route changes propagate via React Router context (bypasses memo).
 */
const RenderFence = memo(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  () => true,
);
RenderFence.displayName = "RenderFence";

// Protected route wrapper when Logto is enabled.
function LogtoProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useLogto();

  if (isAuthenticated) {
    _everAuthenticated = true;
  }

  // Once authenticated, ALWAYS render children — never unmount for token refresh.
  // RenderFence blocks the re-render cascade from LogtoProvider's isLoading toggles.
  if (_everAuthenticated) {
    return <RenderFence>{children}</RenderFence>;
  }

  // First load only: show spinner
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Never authenticated: redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <RenderFence>{children}</RenderFence>;
}

// Protected route wrapper for legacy auth (magic link, OAuth)
function LegacyProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, initAuth } = useAuthStore();

  // ALWAYS initialize auth on mount to validate tokens
  // This is critical because isAuthenticated is NOT persisted
  useEffect(() => {
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  if (isLoading) {
    return <LoadingFallback />;
  }

  // Require authentication - redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Unified protected route - uses Logto when enabled, otherwise legacy
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  if (isLogtoEnabled()) {
    return <LogtoProtectedRoute>{children}</LogtoProtectedRoute>;
  }
  return <LegacyProtectedRoute>{children}</LegacyProtectedRoute>;
}

/**
 * Streaming wrapper — provides LogtoStreamingProvider when Logto is enabled.
 */
function StreamingWrapper({ children }: { children: React.ReactNode }) {
  if (isLogtoEnabled()) {
    return <LogtoStreamingProvider>{children}</LogtoStreamingProvider>;
  }
  return <>{children}</>;
}

/**
 * Layout route for all authenticated pages.
 * ProtectedRoute + StreamingWrapper persist across navigations (/, /profile, /admin).
 * Only the <Outlet /> child swaps — no provider unmount/remount.
 */
function AuthenticatedLayout() {
  return (
    <ProtectedRoute>
      <StreamingWrapper>
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>
      </StreamingWrapper>
    </ProtectedRoute>
  );
}

// App Router
function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/request-access" element={<RequestAccessPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        {/* Magic link verification route */}
        <Route path="/auth/verify" element={<CallbackPage />} />
        {/* Layout route: auth guard + streaming provider persist across children */}
        <Route element={<AuthenticatedLayout />}>
          <Route path="/" element={<App />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// Mount application
const container = document.getElementById("root");

if (!container) {
  throw new Error("Root container not found");
}

const root = createRoot(container);

// Conditionally wrap with LogtoProvider if Logto is enabled
// Use SessionLogtoClient for session-based auth (clears on browser close)
const logtoEnabled = isLogtoEnabled();
const AppWithProviders = logtoEnabled ? (
  <LogtoProvider
    config={getLogtoConfig()}
    LogtoClientClass={SessionLogtoClient}
  >
    <AppRouter />
  </LogtoProvider>
) : (
  <AppRouter />
);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<LoadingFallback />}>{AppWithProviders}</Suspense>
    </QueryClientProvider>
  </StrictMode>,
);
